/**
 * Iter 313 Simplification Pass (2026-07-09) — View Through fail-closed shield.
 *
 * Renders a full-surface overlay whenever the owner is in View Through
 * mode AND the current route is NOT in `VIEW_THROUGH_SUPPORTED_PATHS`.
 * The overlay:
 *   • blocks all clicks and inputs behind it (pointer-events:auto on the
 *     shield, pointer-events:none inherited by the layer below via a
 *     wrapping div),
 *   • states plainly that this surface does not honor the viewed
 *     context — so the operator can never accidentally execute an
 *     action against owner permissions while thinking they're the
 *     viewed user,
 *   • offers a single "Exit View Through" action.
 *
 * This exists BECAUSE the current architecture only migrates 3 backend
 * handlers to `get_permission_user`. Until the parity refactor lands
 * (see /app/memory/BACKLOG_view_through_parity_refactor.md), every
 * other surface would silently execute against the OWNER's permissions
 * — which the owner explicitly does not want.
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, DoorOpen } from "lucide-react";
import {
  useViewThrough,
  isPathSupportedByViewThrough,
  VIEW_THROUGH_SUPPORTED_PATHS,
} from "../../lib/viewThroughContext";

export const ViewThroughUnsupportedShield = () => {
  const { isActive, viewThrough, exit } = useViewThrough();
  const location = useLocation();
  if (!isActive) return null;
  if (isPathSupportedByViewThrough(location.pathname)) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="alertdialog"
      aria-labelledby="vt-shield-title"
      aria-describedby="vt-shield-body"
      data-testid="view-through-unsupported-shield"
    >
      <div className="max-w-lg w-[92%] rounded-lg bg-white shadow-2xl border border-amber-300 overflow-hidden">
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div>
            <h2 id="vt-shield-title" className="font-semibold text-amber-900">
              View Through is not supported on this surface
            </h2>
            <p className="text-xs text-amber-800/80 mt-0.5">
              Fail-closed: actions are disabled here to prevent silent
              execution against owner permissions.
            </p>
          </div>
        </div>
        <div id="vt-shield-body" className="px-5 py-4 text-sm text-gray-700 space-y-3">
          <p>
            You are not seeing{" "}
            <span className="font-semibold">
              {viewThrough?.target_label || "the selected context"}
            </span>
            ’s exact experience on this page. Only the following surface
            genuinely honors the viewed context today:
          </p>
          <ul className="list-disc pl-5 text-xs" data-testid="vt-shield-supported-list">
            {VIEW_THROUGH_SUPPORTED_PATHS.map((p) => (
              <li key={p} className="font-mono">
                {p}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            Exit View Through to continue as the logged-in owner, or
            navigate to a supported surface to test that context.
          </p>
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={exit}
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-sm font-medium"
            data-testid="view-through-shield-exit-btn"
          >
            <DoorOpen className="w-4 h-4" />
            Exit View Through
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewThroughUnsupportedShield;
